import { useState, useEffect, useCallback } from 'react'
import { performanceService } from '@/services/api/performanceService'
import { CollaborativeScheduleData } from '@/components/performance/objective-setting/ScheduleCollaborativeSessionDialog'
import toast from 'react-hot-toast'

interface UseCollaborativeSessionOptions {
  open: boolean
  teamMember: { employee_id: string; display_name: string; job_title: string } | null
  effectiveYear: number
  user: { employeeId?: string; displayName?: string } | null
  setFullTeamMember: (member: any) => void
  onObjectivesChange?: () => Promise<void>
  // Fired whenever this hook mutates or transitions the session (schedule, complete,
  // or lazy auto-complete once the meeting time passes). Callers that keep their own
  // session list — e.g. TeamObjectiveConfiguration's approval gate — wire this to
  // their refresh so their snapshot doesn't go stale and block approvals post-schedule.
  onSessionChange?: () => void | Promise<void>
}

export function useCollaborativeSession({
  open,
  teamMember,
  effectiveYear,
  user,
  setFullTeamMember,
  onObjectivesChange,
  onSessionChange
}: UseCollaborativeSessionOptions) {
  const [collaborativeSession, setCollaborativeSession] = useState<any>(null)
  const [loadingSession, setLoadingSession] = useState(false)
  const [schedulingSession, setSchedulingSession] = useState(false)
  const [completingSession, setCompletingSession] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [scheduleForm, setScheduleForm] = useState({
    date: '',
    time: '',
    duration: '60' as '30' | '60' | '90' | '120',
    location: '',
    meetingType: 'virtual' as 'virtual' | 'face-to-face',
    agenda: ''
  })

  const [canCompleteSession, setCanCompleteSession] = useState(false)

  const fetchCollaborativeSession = useCallback(async () => {
    if (!open || !teamMember) return

    setLoadingSession(true)
    try {
      const sessions = await performanceService.getEmployeeSessions(
        teamMember.employee_id,
        { performance_year: effectiveYear }
      )

      const isActiveStatus = (status: string) =>
        !['completed', 'cancelled'].includes(status)

      const isCompletedStatus = (status: string) =>
        status === 'completed'

      const activeSession = sessions?.find((s: any) => isActiveStatus(s.status))
      const completedSession = sessions?.find((s: any) => isCompletedStatus(s.status))

      setCollaborativeSession(activeSession || completedSession || null)
      if (import.meta.env.DEV) console.log('📋 Loaded collaborative session:', activeSession || completedSession || 'none')
    } catch (error) {
      console.error('Error fetching collaborative session:', error)
      setCollaborativeSession(null)
    } finally {
      setLoadingSession(false)
    }
  }, [open, teamMember, effectiveYear])

  useEffect(() => {
    fetchCollaborativeSession()
  }, [fetchCollaborativeSession])

  useEffect(() => {
    const checkScheduledTime = () => {
      if (!collaborativeSession?.scheduled_date) {
        setCanCompleteSession(false)
        return
      }
      const scheduledDate = new Date(collaborativeSession.scheduled_date)
      const now = new Date()
      const arrived = now >= scheduledDate
      setCanCompleteSession(arrived)

      // Once the start time passes on a still-"scheduled" session, refetch so the
      // backend can flip it to "completed" — this closes the reschedule window and
      // opens objective approvals while the dialog stays open. Also notify callers
      // that keep their own session list so their approval gate sees the completion.
      if (arrived && collaborativeSession.status === 'scheduled') {
        fetchCollaborativeSession()
        onSessionChange?.()
      }
    }

    checkScheduledTime()

    if (collaborativeSession?.status === 'scheduled' && collaborativeSession?.scheduled_date) {
      const scheduledDate = new Date(collaborativeSession.scheduled_date)
      const now = new Date()

      if (now < scheduledDate) {
        const interval = setInterval(checkScheduledTime, 30000)
        return () => clearInterval(interval)
      }
    }
  }, [collaborativeSession?.scheduled_date, collaborativeSession?.status, fetchCollaborativeSession, onSessionChange])

  const handleScheduleSession = async (scheduleData: CollaborativeScheduleData) => {
    if (!teamMember) {
      toast.error('No team member selected')
      return
    }

    setSchedulingSession(true)
    try {
      if (!collaborativeSession) {
        await performanceService.createCollaborativeSession({
          employee_id: teamMember.employee_id,
          employee_name: teamMember.display_name,
          employee_role: teamMember.job_title,
          manager_id: user?.employeeId || 'manager',
          manager_name: user?.displayName || 'People Leader',
          session_type: 'objective_setting',
          performance_year: effectiveYear,
          scheduled_by: user?.employeeId || 'manager'
        })
      }

      const sessions = await performanceService.getEmployeeSessions(
        teamMember.employee_id,
        { performance_year: effectiveYear }
      )
      const session = sessions?.find((s: any) => s.status !== 'completed' && s.status !== 'cancelled')

      if (session) {
        const [hours, minutes] = scheduleData.scheduledTime.split(':').map(Number)
        const scheduledDateTime = new Date(scheduleData.scheduledDate)
        scheduledDateTime.setHours(hours, minutes, 0, 0)

        await performanceService.scheduleCollaborativeSession(session.id, user?.employeeId || session.manager_id || 'manager', {
          scheduled_date: scheduledDateTime.toISOString(),
          meeting_location: scheduleData.location,
          meeting_type: scheduleData.meetingType === 'face-to-face' ? 'face_to_face' : scheduleData.meetingType,
          meeting_agenda: scheduleData.agenda
        })

        const updatedSessions = await performanceService.getEmployeeSessions(
          teamMember.employee_id,
          { performance_year: effectiveYear }
        )
        // If the meeting was scheduled for a time that has already arrived, the list
        // endpoint auto-completes it (see _auto_complete_due_sessions). Mirror the
        // initial-load selection: prefer the active (scheduled) session, but fall back
        // to the just-completed one so the dialog advances to the approval state instead
        // of dropping the session to null and re-showing the "Schedule Meeting" prompt.
        const activeSession = updatedSessions?.find((s: any) => s.status !== 'completed' && s.status !== 'cancelled')
        const completedSession = updatedSessions?.find((s: any) => s.status === 'completed')
        setCollaborativeSession(activeSession || completedSession || null)

        // Keep caller-owned session lists (e.g. the approval gate) in sync so a freshly
        // scheduled — possibly already auto-completed — session doesn't get re-blocked.
        await onSessionChange?.()

        toast.success('Meeting scheduled successfully!')
        setShowScheduleModal(false)
      }
    } catch (error: any) {
      console.error('Error scheduling session:', error)
      toast.error(error.message || 'Failed to schedule meeting')
    } finally {
      setSchedulingSession(false)
    }
  }

  const handleCompleteSession = async () => {
    if (!collaborativeSession) return

    setCompletingSession(true)
    try {
      await performanceService.completeCollaborativeSession(collaborativeSession.id, {
        collaboration_notes: 'Session completed - objectives reviewed and discussed',
        duration_minutes: 60
      })

      if (teamMember) {
        const sessions = await performanceService.getEmployeeSessions(
          teamMember.employee_id,
          { performance_year: effectiveYear }
        )
        const completedSession = sessions?.find((s: any) => s.status === 'completed')
        setCollaborativeSession(completedSession || null)

        // Keep caller-owned session lists (e.g. the approval gate) in sync.
        await onSessionChange?.()

        const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })
        const allObjectives = data.objectives || []
        setFullTeamMember({
          ...teamMember,
          pending_objectives: allObjectives.filter((obj: any) => obj.status === 'pending_approval'),
          active_objectives: allObjectives.filter((obj: any) => obj.status === 'active'),
          rejected_objectives: allObjectives.filter((obj: any) => obj.status === 'rejected')
        })

        if (onObjectivesChange) {
          await onObjectivesChange()
        }
      }

      toast.success('Session marked as complete! You can now approve objectives.')
    } catch (error: any) {
      console.error('Error completing session:', error)
      toast.error(error.message || 'Failed to complete session')
    } finally {
      setCompletingSession(false)
    }
  }

  // Objectives can be approved as soon as the session has STARTED — i.e. the
  // scheduled time has arrived (canCompleteSession), or the session is already
  // in progress / completed. No manual "mark complete" step is required: the
  // session is treated as effectively complete the moment it begins.
  const canApproveObjectives =
    canCompleteSession ||
    collaborativeSession?.status === 'in_progress' ||
    collaborativeSession?.status === 'in-progress' ||
    collaborativeSession?.status === 'completed'

  // Recall/withdraw is already gated per-objective (pending_approval + never-active + owner
  // role) in ObjectiveCard/ObjectiveListTab, so a pending objective can be recalled regardless
  // of collaborative-session status — a scheduled meeting shouldn't lock a pending objective in.
  const canWithdrawObjectives = true

  const resetSessionState = () => {
    setCollaborativeSession(null)
    setShowScheduleModal(false)
    setScheduleForm({ date: '', time: '', duration: '60', location: '', meetingType: 'virtual', agenda: '' })
  }

  return {
    collaborativeSession,
    setCollaborativeSession,
    loadingSession,
    schedulingSession,
    completingSession,
    showScheduleModal,
    setShowScheduleModal,
    scheduleForm,
    setScheduleForm,
    canCompleteSession,
    canApproveObjectives,
    canWithdrawObjectives,
    handleScheduleSession,
    handleCompleteSession,
    resetSessionState
  }
}
